import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { formatTry } from '../../utils/finance/financeFormat';
import { GIDER_KALEMLERI } from '../../utils/finance/financeCostConstants';
import { expenseAmount, isActiveExpense, periodExpenseAmount } from '../../utils/finance/financePnl';
import ExpenseModal from './ExpenseModal';
import FinanceKpiCard from './FinanceKpiGrid';
import { fx, fxCardSx, fxPrimaryBtnSx } from './financeTheme';

function kalemLabel(id) {
  return GIDER_KALEMLERI.find((k) => k.id === id)?.label || id;
}

const thSx = {
  textAlign: 'left',
  fontSize: 11.5,
  fontWeight: 700,
  color: fx.muted,
  p: '5px 8px',
  borderBottom: `1px solid ${fx.border}`,
  whiteSpace: 'nowrap'
};

const tdSx = {
  p: '5px 8px',
  fontSize: 12.5,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  borderBottom: `1px solid ${fx.border}`
};

function ExpenseColumn({ rows, range, canEdit, onEdit, onCancel }) {
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" sx={{ ...thSx, width: '34%' }}>Gider</Box>
          <Box component="th" sx={{ ...thSx, width: '16%' }}>Kategori</Box>
          <Box component="th" sx={{ ...thSx, width: '20%' }}>Aylık</Box>
          <Box component="th" sx={{ ...thSx, width: '20%' }}>Dönem</Box>
          <Box component="th" sx={{ ...thSx, width: 52, p: '5px 4px' }} />
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((row) => (
          <Box
            component="tr"
            key={row.id}
            onClick={() => canEdit && onEdit(row)}
            sx={{ cursor: canEdit ? 'pointer' : 'default', '&:hover td': { bgcolor: '#fafbfc' } }}
          >
            <Box component="td" sx={{ ...tdSx, fontWeight: 700 }}>{row.title || row.description}</Box>
            <Box component="td" sx={tdSx}>{kalemLabel(row.category)}</Box>
            <Box component="td" sx={{ ...tdSx, fontWeight: 800 }}>{formatTry(expenseAmount(row))}</Box>
            <Box component="td" sx={{ ...tdSx, fontWeight: 800 }}>{formatTry(periodExpenseAmount(row, range))}</Box>
            <Box component="td" sx={{ ...tdSx, textAlign: 'right', p: '2px 4px' }}>
              {canEdit ? (
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onCancel(row.id); }}
                  sx={{ color: fx.danger, fontWeight: 700, textTransform: 'none', minWidth: 0, px: 0.6, fontSize: 12 }}
                >
                  İptal
                </Button>
              ) : null}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function FinanceExpenses({
  giderler,
  range,
  canEdit,
  personeller,
  onAdd,
  onUpdate,
  onCancel,
  loading
}) {
  const [modal, setModal] = useState({ open: false, row: null });
  const active = useMemo(() => giderler.filter(isActiveExpense), [giderler]);
  const monthly = active.reduce((s, g) => s + expenseAmount(g), 0);
  const period = active.reduce((s, g) => s + periodExpenseAmount(g, range), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15, minHeight: 0, flex: 1, overflow: 'hidden' }} data-testid="finance-expenses">
      <Box sx={{ display: 'flex', gap: 1.15, alignItems: 'stretch', flexWrap: 'nowrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}><FinanceKpiCard label="Aylık gider" value={formatTry(monthly)} toneKey="gider" hint="Kayıtlı aylık tutar." /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}><FinanceKpiCard label="Bu döneme düşen" value={formatTry(period)} toneKey="ciro" hint="Aylık tutarın seçilen güne / aralığa payı." /></Box>
        {canEdit ? (
          <Button variant="contained" onClick={() => setModal({ open: true, row: null })} sx={{ ...fxPrimaryBtnSx, px: 2, whiteSpace: 'nowrap', alignSelf: 'stretch' }}>
            + Gider Ekle
          </Button>
        ) : null}
      </Box>
      <Box sx={{ ...fxCardSx, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Typography sx={{ p: 2.5, fontSize: 13.5, color: fx.faint }}>Yükleniyor…</Typography>
        ) : active.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: fx.text }}>Kayıtlı gider yok.</Typography>
            <Typography sx={{ fontSize: 13, color: fx.muted, mt: 0.6 }}>Aylık tutar ekleyin; günlük pay otomatik hesaplanır.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              minHeight: 0,
              flex: 1,
              overflow: 'hidden',
              alignContent: 'start'
            }}
          >
            <Box sx={{ minWidth: 0, borderRight: `1px solid ${fx.border}`, pr: 0.4 }}>
              <ExpenseColumn
                rows={active.slice(0, Math.ceil(active.length / 2))}
                range={range}
                canEdit={canEdit}
                onEdit={(row) => setModal({ open: true, row })}
                onCancel={onCancel}
              />
            </Box>
            <Box sx={{ minWidth: 0, pl: 0.4 }}>
              <ExpenseColumn
                rows={active.slice(Math.ceil(active.length / 2))}
                range={range}
                canEdit={canEdit}
                onEdit={(row) => setModal({ open: true, row })}
                onCancel={onCancel}
              />
            </Box>
          </Box>
        )}
      </Box>
      <ExpenseModal
        open={modal.open}
        initial={modal.row}
        personeller={personeller}
        onClose={() => setModal({ open: false, row: null })}
        onSave={async (form) => {
          if (modal.row) await onUpdate(modal.row.id, form);
          else await onAdd(form);
          setModal({ open: false, row: null });
        }}
      />
    </Box>
  );
}
